import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AccountAvatar,
  Fab,
  TextField,
  Button,
  LoadingBounce,
} from '@michelin/acid-components';
import { FileCopy } from '@material-ui/icons';
import useCopyServicesLocationList from 'hooks/useCopyServices';
import { Autocomplete } from '@material-ui/lab';
import {
  Dialog, DialogActions, DialogContent, DialogTitle,
} from '@material-ui/core';
import {
  FieldValues,
  Path, UseFormSetValue,
} from 'react-hook-form';
import { UseQueryResult } from 'react-query';

interface CopyServicesProps<T extends FieldValues = FieldValues> {
  type: 'tires' | 'mechanical' | 'services',
  setValue: UseFormSetValue<T>,
  getServicesHook: (locationNumber: string | null) => UseQueryResult<T>;
  dataPath?: Path<T>[],
}

function setRecursiveValues<T extends FieldValues = FieldValues>(path: string, data: any, setValue: UseFormSetValue<T>) {
  if (!data || Array.isArray(data) || typeof data !== 'object') {
    setValue(path as any, data, { shouldDirty: true });
    return;
  }
  Object.keys(data).forEach((key) => {
    if (key === 'epartner') return;
    const newPath = path ? `${path}.${key}` : key;
    setRecursiveValues(newPath, data[key], setValue);
  });
}

function CopyServices<T extends FieldValues = FieldValues>({
  type, setValue, getServicesHook, dataPath,
}: CopyServicesProps<T>) {
  const { t } = useTranslation();
  const { btNumber } = useParams<{ btNumber: string }>();
  const locations = useCopyServicesLocationList(btNumber, type);
  const [open, setOpen] = useState(false);
  const [locationNumber, setLocationNumber] = useState<string | null>(null);
  const newServices = getServicesHook(locationNumber);

  if (locations.isLoading || locations.isError || !locations.data) return null;
  if (!locations.data.length) return null;

  const copy = async () => {
    if (!newServices.data) return;
    if (!dataPath) {
      setRecursiveValues('', newServices.data, setValue);
    } else {
      dataPath.forEach((path) => {
        setRecursiveValues(path, (newServices.data as any)[path], setValue);
      });
    }
    setOpen(false);
  };

  return (
    <>
      <Fab
        color="primary"
        aria-label={t('Copy Services from Other Location')}
        onClick={() => { setLocationNumber(null); setOpen(true); }}
      >
        <FileCopy />
      </Fab>
      <Dialog
        onClose={() => setOpen(false)}
        aria-labelledby="customized-dialog-title"
        open={open}
        fullWidth
        maxWidth="sm"
        scroll="paper"
      >
        <DialogTitle>
          {t('Copy Services from other location')}
        </DialogTitle>
        <DialogContent dividers>
          <Autocomplete
            style={{ width: '100%' }}
            options={locations.data}
            onChange={(e: any, v: any) => setLocationNumber(v?.ship_to_number || null)}
            getOptionLabel={(c: any) => `ST #${c.ship_to_number} - ${c.customer_dba_name || c.customer_name} (${c.customer_city}, ${c.customer_state})`}
            renderOption={(c: any) => (
              <AccountAvatar
                address={{ city: c.customer_city, state: c.customer_state }}
                addressDisplay="cityState"
                name={c.customer_dba_name || c.customer_name}
                accountType="ST"
                accountNumbers={{ shipTo: c.ship_to_number }}
                style={{ cursor: 'pointer' }}
              />
            )}
            // eslint-disable-next-line react/jsx-props-no-spreading
            renderInput={(params) => <TextField {...params} label="Select a Location to Copy Services From" />}
          />
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            color="primary"
            style={{ margin: '8px' }}
            onClick={copy}
            disabled={!newServices.data}
          >
            {newServices.isFetching ? <LoadingBounce style={{ zoom: 0.8, height: 28, paddingTop: 2 }} /> : t('Copy')}
          </Button>
          <Button
            size="small"
            color="secondary"
            style={{ margin: '8px' }}
            onClick={() => setOpen(false)}
          >
            {t('Close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default CopyServices;
